import { supabase } from "../supabaseClient";
import {
  BRAND_CACHE_KEY,
  BRAND_CACHE_TTL,
  BRAND_DEFAULTS,
} from "../constants/brandingDefaults";

const hasWindow = typeof window !== "undefined";

const normalizeRgbValue = (value, fallback) => {
  const target = value || fallback || "";
  return target.replace(/,/g, " ").replace(/\s+/g, " ").trim();
};

const normalizeBranding = (record = BRAND_DEFAULTS) => {
  const data = record || BRAND_DEFAULTS;
  const primaryRgb = normalizeRgbValue(
    data.primary_color_rgb,
    BRAND_DEFAULTS.primary_color_rgb
  );
  const accentRgb = normalizeRgbValue(
    data.accent_color_rgb,
    BRAND_DEFAULTS.accent_color_rgb
  );

  return {
    slug: data.slug || "default",
    colors: {
      primary: data.primary_color || BRAND_DEFAULTS.primary_color,
      primaryRgb,
      accent: data.accent_color || BRAND_DEFAULTS.accent_color,
      accentRgb,
    },
    logos: {
      dark: data.logo_dark_url || BRAND_DEFAULTS.logo_dark_url,
      light: data.logo_light_url || BRAND_DEFAULTS.logo_light_url,
      icon: data.icon_url || BRAND_DEFAULTS.icon_url,
    },
    meta: {
      ogImage: data.og_image_url || BRAND_DEFAULTS.og_image_url,
    },
    extras: {
      whatsappLogo:
        data.whatsapp_logo_url ||
        data.icon_url ||
        BRAND_DEFAULTS.whatsapp_logo_url,
    },
    raw: data,
  };
};

const applyCssVariables = (branding) => {
  if (!hasWindow) return;
  const root = document.documentElement;
  const { colors } = branding;
  root.style.setProperty("--brand", colors.primary);
  root.style.setProperty("--brand-rgb", colors.primaryRgb);
  root.style.setProperty("--accent", colors.accent);
  root.style.setProperty("--accent-rgb", colors.accentRgb);
};

const readCache = () => {
  if (!hasWindow) return null;
  try {
    const cached = window.localStorage.getItem(BRAND_CACHE_KEY);
    if (!cached) return null;
    const payload = JSON.parse(cached);
    if (!payload?.data || !payload?.ts) return null;
    if (Date.now() - payload.ts > BRAND_CACHE_TTL) return null;
    return payload.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(
      BRAND_CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    // ignore
  }
};

export async function fetchBrandingSettings(slug = "default") {
  const { data, error } = await supabase
    .from("brand_settings")
    .select("*")
    .eq("slug", slug)
    .single();
  if (error) throw error;
  return data;
}

export function loadBrandingFromCache() {
  return readCache();
}

export function cacheBranding(data) {
  writeCache(data);
}

export function applyBrandingToCss(record) {
  applyCssVariables(normalizeBranding(record));
}

export function getDefaultBranding() {
  return normalizeBranding(BRAND_DEFAULTS);
}

export function toBranding(record) {
  return normalizeBranding(record);
}
